import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Store, CalendarDays, ShieldCheck } from "lucide-react";
import { Logo } from "./Logo";
import { StoreButtons } from "./StoreButtons";
import { PhoneMockup } from "./PhoneMockup";

const EASE = [0.22, 1, 0.36, 1];
const LINES = [
    <>The social network</>,
    <>
        for <em className="italic text-[#4A5D23]">real life</em>.
    </>,
];

export const Hero = ({ socialOpen, onToggleSocial }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const phoneY = useTransform(scrollYProgress, [0, 1], [0, 70]);
    const chipY = useTransform(scrollYProgress, [0, 1], [0, -40]);

    return (
        <section ref={ref} className="relative overflow-hidden" data-testid="hero-section">
            <header data-testid="hero-header" className="mx-auto flex max-w-6xl flex-col items-start gap-4 px-6 pt-7 sm:flex-row sm:items-center sm:justify-between">
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                >
                    <Logo size={46} data-testid="hero-lunar-logo" />
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.15, ease: EASE }}
                    className="flex flex-wrap items-start gap-2 sm:flex-row sm:items-center"
                >
                    <a
                        href="/events"
                        data-testid="header-events-link"
                        className="flex items-center gap-1.5 rounded-full border border-[#C2C0B8] px-3 py-2 text-[11px] font-medium text-[#3E413B] transition-[border-color,color,transform] duration-300 hover:-translate-y-[1px] hover:border-[#899B6A] hover:text-[#2C3618] sm:px-4 sm:text-xs"
                    >
                        <CalendarDays className="h-3.5 w-3.5 shrink-0 text-[#4A5D23]" strokeWidth={1.75} />
                        <span className="whitespace-nowrap">For events</span>
                    </a>
                    <a
                        href="/venue-kit"
                        data-testid="header-venue-link"
                        className="flex items-center gap-1.5 rounded-full border border-[#C2C0B8] px-3 py-2 text-[11px] font-medium text-[#3E413B] transition-[border-color,color,transform] duration-300 hover:-translate-y-[1px] hover:border-[#899B6A] hover:text-[#2C3618] sm:px-4 sm:text-xs"
                    >
                        <Store className="h-3.5 w-3.5 shrink-0 text-[#4A5D23]" strokeWidth={1.75} />
                        <span className="whitespace-nowrap">For venues</span>
                    </a>
                    <a
                        href="/safety"
                        data-testid="header-safety-link"
                        className="flex items-center gap-1.5 rounded-full border border-[#C2C0B8] px-3 py-2 text-[11px] font-medium text-[#3E413B] transition-[border-color,color,transform] duration-300 hover:-translate-y-[1px] hover:border-[#899B6A] hover:text-[#2C3618] sm:px-4 sm:text-xs"
                    >
                        <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-[#4A5D23]" strokeWidth={1.75} />
                        <span className="whitespace-nowrap">Safety →</span>
                    </a>
                </motion.div>
            </header>

            <div className={`mx-auto grid max-w-6xl gap-14 px-6 pb-20 pt-14 sm:pt-20 lg:gap-8 lg:pb-28 lg:pt-24 ${socialOpen ? "lg:grid-cols-[1.05fr_0.95fr] lg:items-center" : ""}`}>
                <div>
                    <h1
                        data-testid="hero-headline"
                        className="font-display text-[2.9rem] font-medium leading-[1.04] tracking-tight text-[#1D201A] sm:text-6xl lg:text-7xl"
                    >
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className="block"
                                    initial={{ y: "112%" }}
                                    animate={{ y: "0%" }}
                                    transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: EASE }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.55, ease: EASE }}
                    >
                        <p data-testid="hero-subheadline" className="mt-6 text-lg text-[#3E413B] sm:text-xl">
                            Lunar helps people find each other, socially and professionally.
                        </p>
                        <div className="mt-9">
                            <StoreButtons
                                appStoreTestId="hero-download-app-store"
                                googlePlayTestId="hero-download-google-play"
                            />
                        </div>
                        <div className="mt-10 grid gap-3 sm:grid-cols-2" data-testid="pathway-fork">
                            <button
                                type="button"
                                data-testid="fork-social"
                                onClick={onToggleSocial}
                                aria-expanded={socialOpen}
                                className={`rounded-2xl border p-5 text-left transition-colors duration-300 ${
                                    socialOpen
                                        ? "border-[#2C3618] bg-[#F0EFEB]"
                                        : "border-[#E5E3DC] hover:border-[#899B6A]"
                                }`}
                            >
                                <span className="font-display text-xl font-semibold text-[#1D201A]">Social</span>
                                <span className="mt-1 block text-sm text-[#5C5F58]">Meet people around you.</span>
                                <span className="mt-3 block text-xs font-bold uppercase tracking-[0.14em] text-[#4A5D23]">
                                    {socialOpen ? "Hide Social ↑" : "Explore Social →"}
                                </span>
                            </button>
                            <a
                                href="/networking"
                                data-testid="fork-networking"
                                className="rounded-2xl border border-[#E5E3DC] p-5 transition-colors duration-300 hover:border-[#899B6A]"
                            >
                                <span className="font-display text-xl font-semibold text-[#1D201A]">Network</span>
                                <span className="mt-1 block text-sm text-[#5C5F58]">Meet people professionally.</span>
                                <span className="mt-3 block text-xs font-bold uppercase tracking-[0.14em] text-[#4A5D23]">
                                    Explore Network →
                                </span>
                            </a>
                        </div>
                    </motion.div>
                </div>

                {socialOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 36 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                        className="relative flex scroll-mt-6 justify-center lg:justify-end"
                        id="hero-phone-wrap"
                        data-testid="hero-phone-wrap"
                    >
                        <motion.div style={{ y: phoneY }}>
                            <PhoneMockup
                                eager
                                src="/assets/screens/social-home-lunar.webp"
                                alt="The Lunar Social app — choose who you’re with and check in"
                            />
                        </motion.div>
                        <motion.div
                            style={{ y: chipY }}
                            className="absolute -left-2 top-16 hidden lg:block"
                            aria-hidden="true"
                        >
                            <motion.div
                                animate={{ y: [0, -8, 0] }}
                                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                                className="rounded-full border border-[#E5E3DC] bg-[#F9F8F4] px-4 py-2 text-xs font-medium text-[#3E413B] shadow-[0_18px_36px_-20px_rgba(29,32,26,0.3)]"
                            >
                                <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-[#4A5D23]" />
                                Checked in · Lunar’s Café
                            </motion.div>
                        </motion.div>
                    </motion.div>
                )}
            </div>
        </section>
    );
};
